/**
 * Invoice and Receipt Generation Utilities
 * Supports: Professional PDF-style invoices, 80mm thermal printer receipts
 */
import { fmtCurrency, getCurrencySymbol } from './localeUtils';
import { publicImageUrl } from './media';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]
));

const publicReceiptUrl = (path, fallback) => {
  if (path && typeof window !== 'undefined') return `${window.location.origin}${path}`;
  return fallback || '';
};

// Get pharmacy branding from settings
const getPharmacyBranding = () => {
  try {
    const settings = JSON.parse(localStorage.getItem('pharmxai_settings') || '{}');
    return {
      name: settings.business_name || 'DutiLyne',
      tagline: settings.tagline || 'AI-Powered Pharmacy Management',
      address: settings.business_address || '',
      phone: settings.contact_phone || '',
      email: settings.contact_email || '',
      logo_url: publicImageUrl(settings.logo_url || ''),
      primary_color: settings.primary_color || '#14b8a6',
    };
  } catch {
    return {
      name: 'DutiLyne',
      tagline: 'AI-Powered Pharmacy Management',
      address: '',
      phone: '',
      email: '',
      logo_url: '',
      primary_color: '#14b8a6',
    };
  }
};

/**
 * Generate Professional A4 Invoice HTML (for PDF printing)
 */
export function generateProfessionalInvoice(sale, options = {}) {
  const branding = getPharmacyBranding();
  const { showCost = false } = options; // Only show cost for admin users
  const hasReturn = Boolean(sale.return_status) && Number(sale.total_returned || 0) > 0;
  const netTotal = sale.effective_total ?? sale.total;
  const portalUrl = publicReceiptUrl(sale.portal_path, sale.portal_url);
  const portalQrUrl = publicReceiptUrl(sale.portal_qr_path, sale.portal_qr_url);
  const portalBlock = portalUrl && portalQrUrl ? `
    <div class="portal-qr">
      <div><strong>Need a refill or pharmacist advice?</strong><br><span>Scan to open ${escapeHtml(sale.portal_branch_name || 'this branch')} patient portal.</span></div>
      <img src="${escapeHtml(portalQrUrl)}" alt="Branch patient portal QR code" />
    </div>` : '';
  
  const itemsHTML = sale.items?.map((item, i) => {
    const amount = item.quantity * item.unit_price;
    const costLine = showCost && item.unit_cost 
      ? `<small style="color:#999;font-size:10px;">(Cost: ${fmtCurrency(item.unit_cost)})</small>` 
      : '';
    return `
      <tr>
        <td style="padding:12px 8px;border-bottom:1px solid #eee;">${i + 1}</td>
        <td style="padding:12px 8px;border-bottom:1px solid #eee;">
          <strong>${item.product_name}</strong>
          ${item.batch_number ? `<br><small style="color:#666;">Batch: ${item.batch_number}</small>` : ''}
          ${costLine}
        </td>
        <td style="padding:12px 8px;border-bottom:1px solid #eee;text-align:center;">${item.quantity}</td>
        <td style="padding:12px 8px;border-bottom:1px solid #eee;text-align:right;">${fmtCurrency(item.unit_price)}</td>
        <td style="padding:12px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:500;">${fmtCurrency(amount)}</td>
      </tr>
    `;
  }).join('') || '';

  const profitSection = showCost && sale.gross_profit !== undefined ? `
    <tr style="background:#f0fdf4;">
      <td colspan="4" style="padding:8px;text-align:right;color:#666;">COGS:</td>
      <td style="padding:8px;text-align:right;color:#666;">${fmtCurrency(sale.total_cost)}</td>
    </tr>
    <tr style="background:#f0fdf4;">
      <td colspan="4" style="padding:8px;text-align:right;font-weight:bold;color:#16a34a;">Gross Profit:</td>
      <td style="padding:8px;text-align:right;font-weight:bold;color:#16a34a;">${fmtCurrency(sale.gross_profit)}</td>
    </tr>
  ` : '';

  const prescriptionInfo = sale.prescription_info ? `
    <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:12px;margin:16px 0;">
      <p style="margin:0;font-size:12px;color:#92400e;"><strong>⚕️ Prescription Sale</strong></p>
      ${sale.prescription_info.doctorName ? `<p style="margin:4px 0 0;font-size:11px;color:#92400e;">Prescribing Doctor: Dr. ${sale.prescription_info.doctorName}</p>` : ''}
      ${sale.prescription_info.prescriptionDate ? `<p style="margin:4px 0 0;font-size:11px;color:#92400e;">Prescription Date: ${sale.prescription_info.prescriptionDate}</p>` : ''}
    </div>
  ` : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${sale.sale_id}</title>
  <style>
    @page { size: A4; margin: 20mm; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    * { box-sizing: border-box; }
    body { 
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif; 
      font-size: 13px; 
      color: #333; 
      line-height: 1.5;
      margin: 0;
      padding: 20px;
    }
    .invoice-container { max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid ${branding.primary_color}; padding-bottom: 20px; margin-bottom: 30px; }
    .logo-section h1 { margin: 0; font-size: 28px; color: ${branding.primary_color}; }
    .logo-section p { margin: 4px 0 0; color: #666; font-size: 12px; }
    .invoice-details { text-align: right; }
    .invoice-details h2 { margin: 0; font-size: 24px; color: #333; text-transform: uppercase; letter-spacing: 2px; }
    .invoice-details p { margin: 4px 0; font-size: 12px; color: #666; }
    .invoice-details .invoice-number { font-family: monospace; font-size: 14px; font-weight: bold; color: #333; }
    .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .party { flex: 1; }
    .party h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #999; margin: 0 0 8px; }
    .party p { margin: 2px 0; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    thead th { 
      background: ${branding.primary_color}; 
      color: white; 
      padding: 12px 8px; 
      text-align: left; 
      font-size: 11px; 
      text-transform: uppercase; 
      letter-spacing: 0.5px; 
    }
    thead th:nth-child(3), thead th:nth-child(4), thead th:nth-child(5) { text-align: right; }
    thead th:nth-child(3) { text-align: center; }
    .totals { margin-left: auto; width: 300px; }
    .totals table { margin-bottom: 0; }
    .totals td { padding: 8px; border: none; }
    .totals .grand-total { background: ${branding.primary_color}; color: white; font-size: 16px; font-weight: bold; }
    .notes { background: #f8f9fa; border-radius: 6px; padding: 12px; margin: 20px 0; font-size: 12px; }
    .footer { text-align: center; padding-top: 30px; border-top: 1px solid #eee; margin-top: 30px; color: #666; font-size: 11px; }
    .portal-qr { display:flex; align-items:center; justify-content:center; gap:18px; margin:28px auto 6px; padding:14px; border:1px solid #d1d5db; max-width:440px; text-align:left; page-break-inside:avoid; }
    .portal-qr img { width:92px; height:92px; object-fit:contain; }
    .portal-qr span { color:#6b7280; font-size:11px; }
  </style>
</head>
<body>
  <div class="invoice-container">
    <div class="header">
      <div class="logo-section">
        ${branding.logo_url ? `<img src="${branding.logo_url}" alt="Logo" style="max-height:60px;margin-bottom:8px;">` : ''}
        <h1>${branding.name}</h1>
        <p>${branding.tagline}</p>
        ${branding.address ? `<p>${branding.address}</p>` : ''}
        ${branding.phone ? `<p>Tel: ${branding.phone}</p>` : ''}
        ${branding.email ? `<p>Email: ${branding.email}</p>` : ''}
      </div>
      <div class="invoice-details">
        <h2>Invoice</h2>
        <p class="invoice-number">${sale.sale_id}</p>
        <p>Date: ${new Date(sale.created_at).toLocaleDateString()}</p>
        <p>Time: ${new Date(sale.created_at).toLocaleTimeString()}</p>
        ${sale.origin === 'online' ? `<p><strong>Online · ${({ otc: 'OTC', refill: 'Refill', prescription: 'Prescription' })[sale.online_request_type] || 'Portal'}</strong></p>` : ''}
        ${hasReturn ? `<p style="color:#dc2626;font-weight:700;text-transform:uppercase;">${sale.return_status === 'full' ? 'Fully Returned' : 'Partially Returned'}</p>` : ''}
      </div>
    </div>

    <div class="parties">
      <div class="party">
        <h3>Bill To</h3>
        <p><strong>${sale.customer_name || 'Walk-in Customer'}</strong></p>
        ${sale.customer_phone ? `<p>Phone: ${sale.customer_phone}</p>` : ''}
      </div>
      <div class="party" style="text-align:right;">
        <h3>Payment Method</h3>
        <p style="text-transform:capitalize;"><strong>${sale.payment_method}</strong></p>
      </div>
    </div>

    ${prescriptionInfo}

    <table>
      <thead>
        <tr>
          <th style="width:40px;">#</th>
          <th>Item Description</th>
          <th style="width:60px;text-align:center;">Qty</th>
          <th style="width:90px;">Unit Price</th>
          <th style="width:100px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemsHTML}
      </tbody>
    </table>

    <div class="totals">
      <table>
        <tr>
          <td style="text-align:right;">Subtotal:</td>
          <td style="text-align:right;">${fmtCurrency(sale.subtotal)}</td>
        </tr>
        ${sale.discount > 0 ? `
          <tr>
            <td style="text-align:right;color:#dc2626;">Discount:</td>
            <td style="text-align:right;color:#dc2626;">-${fmtCurrency(sale.discount)}</td>
          </tr>
        ` : ''}
        ${hasReturn ? `
          <tr>
            <td style="text-align:right;">Original Total:</td>
            <td style="text-align:right;">${fmtCurrency(sale.total)}</td>
          </tr>
          <tr>
            <td style="text-align:right;color:#dc2626;">${sale.return_status === 'full' ? 'Fully Returned' : 'Returned'}:</td>
            <td style="text-align:right;color:#dc2626;">-${fmtCurrency(sale.total_returned)}</td>
          </tr>
        ` : ''}
        <tr class="grand-total">
          <td style="text-align:right;">${hasReturn ? 'Net Total' : 'Total'}:</td>
          <td style="text-align:right;">${fmtCurrency(hasReturn ? netTotal : sale.total)}</td>
        </tr>
        ${profitSection}
      </table>
    </div>

    ${sale.notes ? `
      <div class="notes">
        <strong>Notes:</strong><br>
        ${sale.notes}
      </div>
    ` : ''}

    ${portalBlock}

    <div class="footer">
      <p>Thank you for your business!</p>
      <p>This is a computer-generated invoice. No signature required.</p>
      <p style="margin-top:10px;">${branding.name} • ${branding.email || ''}</p>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Generate 80mm Thermal Printer Receipt (58mm or 80mm width)
 */
export function generateThermalReceipt(sale, options = {}) {
  const branding = getPharmacyBranding();
  const { width = 80 } = options; // 58 or 80mm
  const isNarrow = width === 58;
  const hasReturn = Boolean(sale.return_status) && Number(sale.total_returned || 0) > 0;
  const netTotal = sale.effective_total ?? sale.total;

  // ── Helpers ──────────────────────────────────────────────────────
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const date = new Date(sale.created_at || Date.now());
  const dateStr = date.toLocaleDateString();
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const cashierName = sale.created_by_name || sale.cashier_name || sale.created_by || '';

  // ── Item rows: name on top, "qty × price ............ total" on bottom ──
  const itemRows = (sale.items || []).map((it) => {
    const qty = Number(it.quantity || 0);
    const price = Number(it.unit_price || 0);
    const lineTotal = qty * price;
    return `
      <div class="item">
        <div class="item-name">${esc(it.product_name || it.name || 'Item')}</div>
        <div class="item-line">
          <span class="qxp">${qty} × ${fmtCurrency(price)}</span>
          <span class="dots"></span>
          <span class="amt">${fmtCurrency(lineTotal)}</span>
        </div>
      </div>`;
  }).join('');

  // ── Optional image-based logo (must be a same-origin URL or data: URI
  //    for the popup to load it). The existing `logo_url` already meets
  //    this in the platform; for blob/data URIs they're cross-origin safe.
  const logoBlock = branding.logo_url
    ? `<img class="logo" src="${esc(branding.logo_url)}" alt="logo" />`
    : '';
  const portalUrl = publicReceiptUrl(sale.portal_path, sale.portal_url);
  const portalQrUrl = publicReceiptUrl(sale.portal_qr_path, sale.portal_qr_url);
  const portalBlock = portalUrl && portalQrUrl ? `
    <div class="portal-block">
      <div class="portal-title">SCAN FOR REFILLS & PHARMACIST HELP</div>
      <img class="portal-qr" src="${esc(portalQrUrl)}" alt="Branch portal QR" />
      <div class="portal-branch">${esc(sale.portal_branch_name || 'Patient portal')}</div>
    </div>` : '';

  // ── Compose ─────────────────────────────────────────────────────
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Receipt ${esc(sale.sale_id || '')}</title>
<style>
  /* ─── Thermal print layout for 80mm / 58mm rolls ───────────── */
  @page { size: ${width}mm auto; margin: 0; }

  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }

  body {
    /* Monospace + tabular numerals = perfect column alignment on
       cheap 203 dpi thermal heads. */
    font-family: "Cascadia Mono", "JetBrains Mono", "Menlo", "Consolas",
                 "Lucida Console", "Courier New", monospace;
    font-variant-numeric: tabular-nums;
    font-size: ${isNarrow ? '10pt' : '11pt'};
    line-height: 1.30;
    width: ${width}mm;
    padding: 3mm 3mm 4mm;
  }

  /* Hard-printable color/borders */
  @media print {
    body, .totals, .total-row, .header, .footer, .item-line {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  /* ─── Header ─── */
  .header { text-align: center; margin-bottom: 2mm; }
  .logo {
    max-width: 18mm;
    max-height: 18mm;
    object-fit: contain;
    display: block;
    margin: 0 auto 1.5mm;
    /* Force darken light logos for thermal head readability */
    filter: contrast(1.1) saturate(0) brightness(0.4);
  }
  .biz-name {
    font-size: ${isNarrow ? '12pt' : '14pt'};
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    margin-bottom: 0.3mm;
  }
  .biz-tag, .biz-meta {
    font-size: ${isNarrow ? '8.5pt' : '9.5pt'};
    line-height: 1.25;
    word-break: break-word;
  }
  .biz-tag { font-style: italic; opacity: 0.9; }

  /* ─── Section dividers (drawn with chars + borders for thermal robustness) ─── */
  .rule  { border-top: 1px dashed #000; margin: 2mm 0; }
  .rule2 { border-top: 2px solid #000; margin: 2mm 0; }

  /* ─── Meta block (date / receipt no / cashier / customer) ─── */
  .meta { font-size: ${isNarrow ? '9pt' : '10pt'}; }
  .meta-row { display: flex; justify-content: space-between; gap: 4mm; }
  .meta-row .k { font-weight: 700; }
  .meta-row .v { text-align: right; }

  /* ─── Items ─── */
  .items-head {
    display: flex; justify-content: space-between;
    font-weight: 700; text-transform: uppercase; font-size: ${isNarrow ? '8.5pt' : '9.5pt'};
    border-bottom: 1px solid #000; padding-bottom: 0.5mm; margin-bottom: 1mm;
  }
  .item { margin-bottom: 1.2mm; page-break-inside: avoid; }
  .item-name {
    font-weight: 600;
    word-break: break-word;
    line-height: 1.25;
  }
  .item-line {
    display: flex; align-items: baseline;
    font-size: ${isNarrow ? '9pt' : '10pt'};
  }
  .item-line .qxp { white-space: nowrap; padding-left: 1mm; }
  .item-line .dots {
    flex: 1 1 auto;
    border-bottom: 1px dotted #999;
    transform: translateY(-0.4em);
    margin: 0 1mm;
    height: 0;
  }
  .item-line .amt { white-space: nowrap; font-weight: 600; }

  /* ─── Totals ─── */
  .totals { font-size: ${isNarrow ? '9.5pt' : '10.5pt'}; }
  .totals .row {
    display: flex; justify-content: space-between;
    padding: 0.4mm 0;
  }
  .totals .row .k { letter-spacing: 0.3px; }
  .totals .row .v { font-weight: 700; }
  .total-row {
    border-top: 2px solid #000; border-bottom: 2px solid #000;
    margin-top: 1mm; padding: 1mm 0; font-size: ${isNarrow ? '12pt' : '13pt'};
    display: flex; justify-content: space-between;
    font-weight: 800;
  }
  .pay-row {
    display: flex; justify-content: space-between;
    margin-top: 1mm; font-size: ${isNarrow ? '9pt' : '10pt'};
  }

  /* ─── Footer ─── */
  .footer {
    text-align: center;
    font-size: ${isNarrow ? '8.5pt' : '9.5pt'};
    margin-top: 2.5mm;
    line-height: 1.3;
  }
  .footer .strong { font-weight: 700; text-transform: uppercase; }
  .footer .note { font-style: italic; margin-top: 1mm; }
  .barcode {
    text-align: center;
    font-family: "Libre Barcode 39", monospace;
    font-size: 26pt;
    margin: 2mm 0 0;
    letter-spacing: 0;
  }
  .doc-id {
    text-align: center;
    font-size: ${isNarrow ? '7.5pt' : '8.5pt'};
    color: #444;
    margin-top: 0.5mm;
  }
  .portal-block { text-align:center; margin:2.5mm 0 1mm; page-break-inside:avoid; }
  .portal-title { font-weight:800; font-size:${isNarrow ? '8pt' : '9pt'}; margin-bottom:1mm; }
  .portal-qr { width:26mm; height:26mm; object-fit:contain; image-rendering:pixelated; }
  .portal-branch { font-size:${isNarrow ? '7.5pt' : '8.5pt'}; margin-top:0.5mm; }
</style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    ${logoBlock}
    <div class="biz-name">${esc(branding.name)}</div>
    ${branding.tagline ? `<div class="biz-tag">${esc(branding.tagline)}</div>` : ''}
    ${branding.address ? `<div class="biz-meta">${esc(branding.address)}</div>` : ''}
    ${branding.phone ? `<div class="biz-meta">Tel: ${esc(branding.phone)}</div>` : ''}
    ${branding.email ? `<div class="biz-meta">${esc(branding.email)}</div>` : ''}
  </div>

  <div class="rule2"></div>

  <!-- Meta -->
  <div class="meta">
    <div class="meta-row"><span class="k">Receipt</span><span class="v">${esc(sale.sale_id || '')}</span></div>
    ${hasReturn ? `<div class="meta-row"><span class="k" style="text-transform:uppercase;">${sale.return_status === 'full' ? 'FULLY RETURNED' : 'PARTIALLY RETURNED'}</span><span class="v"></span></div>` : ''}
    ${sale.origin === 'online' ? `<div class="meta-row"><span class="k">Origin</span><span class="v">Online · ${esc(sale.online_request_type || 'Portal')}</span></div>` : ''}
    <div class="meta-row"><span class="k">Date</span><span class="v">${esc(dateStr)} ${esc(timeStr)}</span></div>
    ${cashierName ? `<div class="meta-row"><span class="k">Cashier</span><span class="v">${esc(cashierName)}</span></div>` : ''}
    ${sale.customer_name ? `<div class="meta-row"><span class="k">Customer</span><span class="v">${esc(sale.customer_name)}</span></div>` : ''}
    ${sale.customer_phone ? `<div class="meta-row"><span class="k">Phone</span><span class="v">${esc(sale.customer_phone)}</span></div>` : ''}
  </div>

  <div class="rule"></div>

  <!-- Items header -->
  <div class="items-head">
    <span>Item</span>
    <span>Total</span>
  </div>

  ${itemRows}

  <div class="rule"></div>

  <!-- Totals -->
  <div class="totals">
    <div class="row"><span class="k">Subtotal</span><span class="v">${fmtCurrency(sale.subtotal || 0)}</span></div>
    ${sale.discount > 0 ? `<div class="row"><span class="k">Discount</span><span class="v">- ${fmtCurrency(sale.discount)}</span></div>` : ''}
    ${sale.tax_amount > 0 ? `<div class="row"><span class="k">Tax</span><span class="v">${fmtCurrency(sale.tax_amount)}</span></div>` : ''}
    ${hasReturn ? `
      <div class="row"><span class="k">Original Total</span><span class="v">${fmtCurrency(sale.total || 0)}</span></div>
      <div class="row"><span class="k">${sale.return_status === 'full' ? 'Fully Returned' : 'Returned'}</span><span class="v">- ${fmtCurrency(sale.total_returned)}</span></div>
    ` : ''}
  </div>
  <div class="total-row">
    <span>${hasReturn ? 'NET TOTAL' : 'TOTAL'}</span>
    <span>${fmtCurrency(hasReturn ? netTotal : (sale.total || 0))}</span>
  </div>
  <div class="pay-row">
    <span class="k">Payment</span>
    <span class="v">${esc((sale.payment_method || '').toUpperCase())}</span>
  </div>

  <div class="rule"></div>

  ${portalBlock}

  <!-- Footer -->
  <div class="footer">
    <div class="strong">Thank you for your purchase!</div>
    <div>Please come again</div>
    <div class="note">Cut and torn medicines were not taken back.</div>
    <div class="doc-id">${esc(sale.sale_id || '')}</div>
  </div>
</body>
</html>`;
}

/**
 * Print professional invoice
 */
export function printProfessionalInvoice(sale, options = {}) {
  const html = generateProfessionalInvoice(sale, options);
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    alert('Please allow popups to print the invoice');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWhenImagesReady(printWindow);
}

/**
 * Print thermal receipt
 */
export function printThermalReceipt(sale, options = {}) {
  const html = generateThermalReceipt(sale, options);
  const printWindow = window.open('', '_blank', 'width=380,height=720');
  if (!printWindow) {
    alert('Please allow popups to print the receipt');
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();

  printWhenImagesReady(printWindow);
}

function printWhenImagesReady(printWindow) {
  const trigger = () => { try { printWindow.print(); } catch { /* noop */ } };
  const wait = () => {
    const pending = [...printWindow.document.querySelectorAll('img')].filter(img => !img.complete);
    if (!pending.length) {
      setTimeout(trigger, 150);
      return;
    }
    let remaining = pending.length;
    const settled = () => {
      remaining -= 1;
      if (remaining === 0) setTimeout(trigger, 100);
    };
    pending.forEach(img => {
      img.addEventListener('load', settled, { once: true });
      img.addEventListener('error', settled, { once: true });
    });
    setTimeout(trigger, 1800);
  };
  if (printWindow.document.readyState === 'complete') wait();
  else printWindow.addEventListener('load', wait, { once: true });
}

/**
 * Download invoice as HTML file (can be converted to PDF by browser)
 */
export function downloadInvoice(sale, options = {}) {
  const html = generateProfessionalInvoice(sale, options);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${sale.sale_id}.html`;
  a.click();
  URL.revokeObjectURL(url);
}
